// ======================================================================
// File: RFQFormDrawer.jsx
// Module: PPE → Procurement → RFQ
//
// Purpose:
// - Create RFQ from Approved PR
// - Follow same drawer pattern as PurchaseRequestFormDrawer
// - UI/UX identical to existing PPE modules
// ======================================================================

import React, { useEffect, useState } from "react";
import Select from "react-select";

import rfqService from "../services/rfqService";
import purchaseRequestService from "../../purchaseRequest/services/purchaseRequestService";

import "../rfq-ui.css";

export default function RFQFormDrawer({
  open,
  onClose,
  onSaved,
}) {

  const [form, setForm] = useState({
    pr_id: "",
    rfq_date: "",
    quote_due_date: "",
    validity_date: "",
    remarks: "",
  });

  const [approvedPRs, setApprovedPRs] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);

  // ==============================================================
  // INITIALIZE DRAWER
  // ==============================================================
  useEffect(() => {
    if (open) {
      initializeDrawer();
      loadApprovedPRs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const initializeDrawer = () => {
    setForm({
      pr_id: "",
      rfq_date: "",
      quote_due_date: "",
      validity_date: "",
      remarks: "",
    });
    setLines([]);
  };

  // ==============================================================
  // LOAD APPROVED PRs
  // ==============================================================
  const loadApprovedPRs = async () => {
    try {
      setLoading(true);

      const res = await rfqService.getApprovedPRs();

      setApprovedPRs(res?.data?.data || []);

    } catch (err) {
      console.error("Failed to load approved PRs", err);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // LOAD PR LINES WHEN PR SELECTED
  // ==============================================================
  const handlePRChange = async (prId) => {

    setForm({ ...form, pr_id: prId });

    if (!prId) {
      setLines([]);
      return;
    }

    try {
      setLoading(true);

      const res = await purchaseRequestService.getPurchaseRequestById(prId);

      const prData = res?.data?.data;

      setLines(prData?.lines || []);

    } catch (err) {
      console.error("Failed to load PR details", err);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // CREATE RFQ
  // ==============================================================
  const handleCreateRFQ = async () => {

    if (!form.pr_id) {
      alert("Please select Approved PR");
      return;
    }

    if (!form.rfq_date) {
      alert("Please select RFQ Date");
      return;
    }

    if (!form.quote_due_date) {
      alert("Please select Quote Due Date");
      return;
    }

    try {
      setLoading(true);

      await rfqService.createRFQ(form);

      alert("RFQ created successfully");

      onSaved?.();

    } catch (err) {
      console.error("RFQ creation failed", err);

      const message =
        err?.response?.data?.message ||
        "Failed to create RFQ";

      alert(message);

    } finally {
      setLoading(false);
    }
  };

  // ==============================================================
  // RENDER
  // ==============================================================
  if (!open) return null;

  return (
    <div className="pr-drawer open">

      {/* HEADER */}
      <div className="pr-drawer-header">
        <h3>Create RFQ</h3>

        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="pr-drawer-body">

        {loading && (
          <div style={{ marginBottom: "10px", fontSize: "13px" }}>
            Processing...
          </div>
        )}

        <div className="form-grid">

          {/* SELECT PR */}
          <div className="form-field">
  <label>Select Approved PR</label>

      <Select
        placeholder="Select Purchase Request"
        isSearchable

        value={
          approvedPRs.find(
            p => p.id === Number(form.pr_id)
          )
            ? {
                value: form.pr_id,
                label:
                  approvedPRs.find(
                    p => p.id === Number(form.pr_id)
                  )?.pr_no
              }
            : null
        }

        options={approvedPRs.map((p) => ({
          value: p.id,
          label: p.pr_no || `PR-${p.id}`
        }))}

        onChange={(selected) =>
          handlePRChange(selected?.value || "")
        }
      />
    </div>

          {/* RFQ DATE */}
          <div className="form-field">
            <label>RFQ Date</label>
            <input
              type="date"
              className="pr-input"
              value={form.rfq_date}
              onChange={(e) =>
                setForm({
                  ...form,
                  rfq_date: e.target.value,
                })
              }
            />
          </div>

          {/* QUOTE DUE DATE */}
          <div className="form-field">
            <label>Quote Due Date</label>
            <input
              type="date"
              className="pr-input"
              value={form.quote_due_date}
              onChange={(e) =>
                setForm({
                  ...form,
                  quote_due_date: e.target.value,
                })
              }
            />
          </div>

          {/* VALIDITY DATE */}
          <div className="form-field">
            <label>Validity Date</label>
            <input
              type="date"
              className="pr-input"
              value={form.validity_date}
              onChange={(e) =>
                setForm({
                  ...form,
                  validity_date: e.target.value,
                })
              }
            />
          </div>

          {/* REMARKS */}
          <div className="form-field full">
            <label>Remarks</label>
            <textarea
              className="pr-input"
              placeholder="Enter remarks"
              value={form.remarks}
              onChange={(e) =>
                setForm({
                  ...form,
                  remarks: e.target.value,
                })
              }
            />
          </div>

        </div>

        {/* PR LINE ITEMS PREVIEW */}
        {lines.length > 0 && (
          <div className="section-header">
            <h4>PR Materials</h4>

            <div className="rfq-table-wrapper">
              <table className="rfq-table">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Qty</th>
                    <th>UOM</th>
                    <th>Required Date</th>
                  </tr>
                </thead>

                <tbody>
                    {lines.map((l, i) => (
                        <tr key={i}>
                        <td>{l.material_name || l.material_id}</td>
                        <td>{l.requested_qty}</td>
                        <td>{l.uom}</td>
                        <td>{l.required_date || "-"}</td>
                        </tr>
                    ))}
                    </tbody>

              </table>
            </div>

          </div>
        )}

      </div>

      {/* FOOTER */}
      <div className="pr-drawer-footer">

        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>

        <button
          className="btn-primary"
          onClick={handleCreateRFQ}
          disabled={loading}
        >
          Create RFQ
        </button>

      </div>

    </div>
  );
}
